import { apiRequest } from "@/lib/api/http";
import {
  DEFAULT_EVENT_CATEGORIES,
  type NotificationCategory,
  type VehicleEventType,
} from "@/lib/notifications/vehicle-events";

/**
 * Per-user toast preferences, stored upstream (`docs/Notifications.MD`). Every type defaults to on,
 * including ones added after the user last saved — so a type missing from the map means `true`.
 */
export interface NotificationPrefs {
  toast: Record<VehicleEventType, boolean>;
  eventTypes: VehicleEventType[];
  categories: Record<string, VehicleEventType[]>;
}

interface RemotePrefsData {
  toast?: Record<string, unknown>;
}

interface RemotePrefsResponse {
  success: true;
  data?: RemotePrefsData;
  event_types?: unknown;
  categories?: unknown;
}

/** Upstream promises real booleans; anything else is dropped so it falls back to the default. */
function mapToast(raw: Record<string, unknown> | undefined): Record<string, boolean> {
  const toast: Record<string, boolean> = {};
  for (const [type, value] of Object.entries(raw ?? {})) {
    if (typeof value === "boolean") toast[type] = value;
  }
  return toast;
}

function mapStringList(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];
}

function mapCategories(value: unknown): Record<string, VehicleEventType[]> {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return { ...DEFAULT_EVENT_CATEGORIES };
  }
  const categories: Record<string, VehicleEventType[]> = {};
  for (const [category, types] of Object.entries(value as Record<string, unknown>)) {
    categories[category] = mapStringList(types);
  }
  return categories;
}

function eventTypesFrom(categories: Record<string, VehicleEventType[]>): VehicleEventType[] {
  return Object.values(categories).flat();
}

export async function getNotificationPreferences(): Promise<NotificationPrefs> {
  const response = await apiRequest<RemotePrefsResponse>("/api/notification-preferences");
  const categories = mapCategories(response.categories);
  const eventTypes = mapStringList(response.event_types);
  return {
    toast: mapToast(response.data?.toast),
    eventTypes: eventTypes.length > 0 ? eventTypes : eventTypesFrom(categories),
    categories,
  };
}

/** Sends only the changed types; returns the full, updated map upstream answers with. */
export async function updateNotificationPreferences(
  changes: Partial<Record<VehicleEventType, boolean>>,
): Promise<Record<VehicleEventType, boolean>> {
  const response = await apiRequest<RemotePrefsResponse>("/api/notification-preferences", {
    method: "PUT",
    body: JSON.stringify({ toast: changes }),
  });
  return mapToast(response.data?.toast);
}

/** Missing from the map — a type the user never touched, or one newer than their save — is on. */
export function isToastEnabled(
  toast: Record<VehicleEventType, boolean> | undefined,
  type: VehicleEventType,
): boolean {
  return toast?.[type] !== false;
}

export type { NotificationCategory };
